import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const SummaryGenerator = ({ routine, dailyLog, categories }) => {
  const [selectedMood, setSelectedMood] = useState('');

  const calculateStats = () => {
    const completedEvents = routine.filter(event => dailyLog[event.id]?.completed);
    const totalEvents = routine.length;
    const completionRate = totalEvents > 0 ? (completedEvents.length / totalEvents) * 100 : 0;

    // Calculate time deviations
    let totalDeviation = 0;
    let deviationCount = 0;
    const deviations = [];

    routine.forEach(event => {
      const log = dailyLog[event.id];
      if (log?.actualTime && log.completed) {
        const ideal = new Date(`2000-01-01T${event.idealTime}:00`);
        const actual = new Date(`2000-01-01T${log.actualTime}:00`);
        const diffMs = actual - ideal;
        const diffMinutes = Math.round(diffMs / (1000 * 60));
        
        totalDeviation += Math.abs(diffMinutes);
        deviationCount++;
        deviations.push({
          event: event,
          deviation: diffMinutes,
          actualTime: log.actualTime
        });
      }
    });

    const avgDeviation = deviationCount > 0 ? Math.round(totalDeviation / deviationCount) : 0;

    // Calculate category stats
    const categoryStats = {};
    Object.keys(categories).forEach(category => {
      const categoryEvents = routine.filter(event => event.category === category);
      const completedCategoryEvents = categoryEvents.filter(event => dailyLog[event.id]?.completed);
      categoryStats[category] = {
        total: categoryEvents.length,
        completed: completedCategoryEvents.length,
        icon: categories[category].icon
      };
    });

    return {
      completionRate,
      avgDeviation,
      deviations,
      categoryStats,
      completedEvents: completedEvents.length,
      totalEvents
    };
  };

  const generateSummary = () => {
    const stats = calculateStats();
    const summary = [];

    // Overall completion
    if (stats.completionRate >= 80) {
      summary.push("Great job! Most events were completed today.");
    } else if (stats.completionRate >= 60) {
      summary.push("Good progress! Some events were completed.");
    } else {
      summary.push("Today was challenging. Let's try to complete more events tomorrow.");
    }

    // Time deviations
    if (stats.avgDeviation > 0) {
      if (stats.avgDeviation <= 15) {
        summary.push("Timing was mostly on track with minimal delays.");
      } else if (stats.avgDeviation <= 30) {
        summary.push("Some events were delayed, but nothing major.");
      } else {
        summary.push("There were significant timing delays today.");
      }
    }

    // Specific deviations
    const lateEvents = stats.deviations.filter(d => d.deviation > 15);
    const earlyEvents = stats.deviations.filter(d => d.deviation < -15);

    if (lateEvents.length > 0) {
      const eventNames = lateEvents.map(d => d.event.title).join(", ");
      summary.push(`${eventNames} were significantly delayed.`);
    }

    if (earlyEvents.length > 0) {
      const eventNames = earlyEvents.map(d => d.event.title).join(", ");
      summary.push(`${eventNames} happened earlier than planned.`);
    }

    // Category insights
    Object.entries(stats.categoryStats).forEach(([category, stat]) => {
      if (stat.total > 0) {
        const completionRate = (stat.completed / stat.total) * 100;
        if (completionRate === 100) {
          summary.push(`${stat.icon} All ${category} events were completed!`);
        } else if (completionRate >= 75) {
          summary.push(`${stat.icon} Most ${category} events were completed.`);
        } else if (completionRate < 50) {
          summary.push(`${stat.icon} Few ${category} events were completed.`);
        }
      }
    });

    return summary.length > 0 ? summary.join(" ") : "No events were logged today.";
  };

  const stats = calculateStats();
  const summary = generateSummary();

  const getMoodEmoji = (mood) => {
    const moods = {
      'excellent': '😊',
      'good': '🙂',
      'okay': '😐',
      'challenging': '😔',
      'difficult': '😢'
    };
    return moods[mood] || '😊';
  };

  return (
    <div>
      <h2>Daily Summary</h2>
      
      <div className="summary-card">
        <div className="summary-title">
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
        
        <div className="summary-stats">
          <div className="stat-item">
            <div className="stat-value">{stats.completedEvents}/{stats.totalEvents}</div>
            <div className="stat-label">Events Completed</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{Math.round(stats.completionRate)}%</div>
            <div className="stat-label">Completion Rate</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{stats.avgDeviation}min</div>
            <div className="stat-label">Avg. Deviation</div>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ marginBottom: '12px', color: 'rgba(255,255,255,0.9)' }}>How was today?</h4>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {['excellent', 'good', 'okay', 'challenging', 'difficult'].map(mood => (
              <button
                key={mood}
                onClick={() => setSelectedMood(mood)}
                style={{
                  background: selectedMood === mood ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  transition: 'all 0.3s ease'
                }}
              >
                {getMoodEmoji(mood)} {mood.charAt(0).toUpperCase() + mood.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ 
          background: 'rgba(255,255,255,0.1)', 
          padding: '16px', 
          borderRadius: '12px',
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          <strong>Summary:</strong> {summary}
        </div>
      </div>

      <div className="card">
        <h3>Detailed Analysis</h3>
        
        <div style={{ marginBottom: '24px' }}>
          <h4>Category Breakdown</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {Object.entries(stats.categoryStats).map(([category, stat]) => (
              <div key={category} className="event-item">
                <div className="event-info">
                  <div className="event-title">
                    <span style={{ marginRight: '8px', fontSize: '20px' }}>
                      {stat.icon}
                    </span>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </div>
                  <div className="event-time">
                    {stat.completed}/{stat.total} completed
                  </div>
                </div>
                <div className="event-actions">
                  {stat.completed === stat.total ? (
                    <CheckCircle size={20} color="#28a745" />
                  ) : stat.completed === 0 ? (
                    <XCircle size={20} color="#dc3545" />
                  ) : (
                    <AlertCircle size={20} color="#ffc107" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {stats.deviations.length > 0 && (
          <div>
            <h4>Timing Analysis</h4>
            <div style={{ display: 'grid', gap: '12px' }}>
              {stats.deviations.map((deviation, index) => (
                <div key={index} className="event-item">
                  <div className="event-info">
                    <div className="event-title">
                      <span style={{ marginRight: '8px', fontSize: '20px' }}>
                        {categories[deviation.event.category].icon}
                      </span>
                      {deviation.event.title}
                    </div>
                    <div className="event-time">
                      Ideal: {deviation.event.idealTime} • Actual: {deviation.actualTime}
                    </div>
                  </div>
                  <div className="event-actions">
                    {deviation.deviation > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#dc3545' }}>
                        <TrendingDown size={16} />
                        +{deviation.deviation}min
                      </div>
                    ) : deviation.deviation < 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#28a745' }}>
                        <TrendingUp size={16} />
                        {deviation.deviation}min
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#28a745' }}>
                        <CheckCircle size={16} />
                        On time
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryGenerator; 